import type { QueryClient } from '@tanstack/react-query';

import { getRangeScheduleAPI } from '../../api/schedule';
/**
 * 일정 목록 쿼리 키는 `lib/scheduleQueryKeys` 한 곳에만 있다. 회원 이름 수정도 같은
 * 목록을 무효화해야 해서, 목록이 두 벌로 갈리지 않게 정의를 그쪽으로 옮겼다.
 */
import { SCHEDULE_LIST_QUERY_KEYS } from '../../lib/scheduleQueryKeys';

/** 일정 쓰기가 바꾸는 수강권 잔여 회차 관련 키. */
const MEMBERSHIP_QUERY_KEYS = [
    ['getMembershipForMemberQuery'],
    ['getAllMembershipForMemberQuery'],
    ['sharedMembership', 'staffEntitlements'],
];

export type ScheduleWriteRefreshContext = {
    centerId?: string;
    authorization?: string;
    /** 등록을 시도한 날짜(KST 'YYYY-MM-DD'). 이 날짜 목록을 캐시와 무관하게 다시 받는다. */
    date?: string;
};

/**
 * 일정 쓰기 뒤 화면 캐시를 되살린다. **성공·실패 모두에서 부른다.**
 *
 * 1:2 등록은 실패해도 앞서 만들어진 일정을 되돌리지 않는다(계약 §배경). 실패 응답을 받았다고
 * 서버 상태가 그대로라고 볼 수 없고, 푸시·챗봇 실패로 나가는 오류에는 부분 성공 필드가
 * 하나도 안 붙는데 정작 그때가 괴리가 가장 크다(계약 §1-4). 그래서 **details 를 보지 않고**
 * 실패 경로에서 무조건 부른다(계약 §2-1).
 *
 * 두 단계로 나뉜다.
 *
 * **(1) 캐시에 있는 것 — 기본값 `invalidateQueries` (stale 표시).**
 * 캐시에 남은 날짜·강사·범위 변형을 stale 로 표시해 **다음에 열 때** 갱신되게 한다.
 *
 * 전에는 `refetchType: 'all'` 이었는데, 그러면 접두 키당 한 건이 아니라 **모든 변형**이
 * 한꺼번에 재요청된다(실측 8건). 성공·실패 양쪽에서 부르므로 반복 등록 시 누적된다.
 * 계약이 요구하는 것은 "실패하면 반드시 다시 받는다"이지 "쌓인 캐시를 전부 지금 갱신한다"가
 * 아니라서, 확실한 한 건은 아래 (2)가 책임지고 나머지는 미룬다.
 *
 * ⚠️ 실측(query-core 4.36.1): 기본 `invalidateQueries` 는 구독 중인 관찰자가 있어도 곧바로
 * 요청을 보내지 않았다. 즉 "지금 열려 있는 뷰가 즉시 갱신된다"고 기대하면 안 된다.
 * 이 경로에서는 문제가 되지 않는다 — 등록 화면(CreateSchedulePage)은 SchedulePage 와 다른
 * 라우트라 목록 쿼리가 애초에 마운트돼 있지 않다. 같은 화면에서 목록을 함께 보여주는 곳이
 * 생기면 그때 이 선택을 다시 봐야 한다.
 *
 * **(2) 캐시에 없는 것 — `fetchQuery`.**
 * `invalidateQueries` 는 **기존 쿼리만 되살릴 뿐 새로 만들지 않는다.** 회원 상세에서 등록
 * 화면으로 바로 들어오면 목록 쿼리가 아예 없고, 전역 `cacheTime` 이 5분(index.tsx:15)이라
 * GC 이후에도 없다. 그때 (1)만으로는 요청이 0건이 되어 계약 §2-1 을 못 지킨다.
 * 그래서 등록을 시도한 **그 날짜**의 목록을 캐시 유무와 무관하게 한 번 받아 온다.
 * 등록 화면은 SchedulePage 와 다른 라우트라 목록 쿼리가 대개 비활성이고, 이 한 건이
 * "실패하면 항상 실제 조회가 나간다"를 보장하는 유일한 경로다.
 *
 * 키·강사 범위는 SchedulePage 의 스마트 뷰와 같은 계약을 쓴다(`selectedCoachId` 없이 센터
 * 전체). 강사로 좁혀 받아 같은 키에 넣으면 "센터 전체"를 뜻하는 캐시가 오염된다.
 *
 * 실패는 삼킨다. 이 조회는 best-effort 보강이고, 정확성은 (1)의 무효화가 이미 보장한다
 * (다음 마운트에서 반드시 다시 받는다). 자동 재시도는 하지 않는다(계약 §3-2).
 */
export const refreshScheduleWriteQueries = (
    queryClient: QueryClient,
    context?: ScheduleWriteRefreshContext,
) => {
    [...SCHEDULE_LIST_QUERY_KEYS, ...MEMBERSHIP_QUERY_KEYS].forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
    });

    const { centerId, authorization, date } = context || {};
    if (!centerId || !authorization || !date) return;

    queryClient
        .fetchQuery({
            queryKey: ['getSmartRangeSchedulesAPI', centerId, date, date],
            queryFn: async () => {
                const res = await getRangeScheduleAPI(centerId, date, date, '', authorization);
                if (res?.status !== 200) {
                    throw new Error('일정을 불러오지 못했습니다.');
                }
                return Array.isArray(res?.data?.schedules) ? res.data.schedules : [];
            },
            staleTime: 0,
        })
        .catch(() => {
            // 목록 확인은 사용자가 직접 한다. 여기서 재시도하지 않는다.
        });
};

export const SCHEDULE_WRITE_INVALIDATED_QUERY_KEYS = [
    ...SCHEDULE_LIST_QUERY_KEYS,
    ...MEMBERSHIP_QUERY_KEYS,
];
